import React, { useEffect, useState } from 'react';
import './CampaignList.scss';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import CustomBreadcrumb from '../../../components/base/CustomBreadcrumb';
import CustomListComponent from '../../../components/base/CustomListComponent';
import { campaignListBreads } from '../../../config/CustomBreadConfig';
import CampaignOverviewTab from '../../../components/campaign/campaignList/CampaignOverviewTab';
import { getHashKeyString, createAction } from '../../../utils';
import {
  URLFragments,
  URLFilterKeys,
} from '../../../components/campaign/CampaignUrlConfig';
import {
  CampaignType,
  DELETE_RELATED_SECTIONS,
} from '../../../config/CustomEnums';
import DateRangeSelection from '../../../components/dashboard/DateRangeSelection';
import DateRangeCompare from '../../../components/dashboard/DateRangeCompare';
import AuthButton from '../../../components/base/AuthButton';
import { PermissionCodes } from '../../../config/PermissionCodes';
import BaseTabListContainer from '../../../containers/base/BaseTabListContainer';
import BaseListContainer from '../../../containers/base/BaseListContainer';
import DeleteCampaignPrompt from '../../../components/campaign/campaignList/DeleteCamapignPrompt';
import queryString from 'query-string';
import BasePrompt from '../../../components/base/prompt/BasePrompt';
import Filter from './Filter';
import CampaignEditStorePrompt from '../../../components/campaign/CampaignEditStorePrompt';

const TabsConfig = [
  { key: CampaignType.allTypes, name: 'All Types' },
  { key: CampaignType.couponsCampaign, name: 'Coupon' },
  { key: CampaignType.earningRulesCampaign, name: 'Earning' },
  { key: CampaignType.generalMessageCampaign, name: 'General Message' },
];

function CampaignList() {
  const dispatch = useDispatch();
  const [showPrompt, setShowPrompt] = useState(false);
  const [selectedCampaignID, setSelectedCampaignID] = useState(null);
  const [showEditStorePrompt, setShowEditStorePrompt] = useState(false);
  const history = useHistory();
  const location = useLocation();
  const activeTab = getHashKeyString(location.hash) || URLFragments.overview;
  const {
    listFields,
    dataList,
    pageInfo,
    totalPage,
    totalCount,
    isSuperuser,
    userPermissions,
    resume,
  } = useSelector((state) => ({
    listFields: state.campaignList.listFields,
    dataList: state.campaignList.campaignList,
    pageInfo: state.campaignList.pageInfo,
    totalPage: state.campaignList.totalPage,
    totalCount: state.campaignList.totalCount,
    checkedList: state.campaignList.checkedList,
    isSuperuser: state.users.isSuperuser,
    userPermissions: state.users.userPermissions,
    resume: state.campaignList.resume,
  }));

  const type =
    queryString.parse(location.search)[URLFilterKeys.type] ||
    CampaignType.allTypes;

  const getFilterMapFromUrl = () => {
    const querySearch = queryString.parse(location.search);
    const searchKey = queryString.parse(location.search)[
      URLFilterKeys.searchKey
    ];
    const stringRank = queryString.parse(location.search)[URLFilterKeys.rank];
    const stringPage =
      queryString.parse(location.search)[URLFilterKeys.page] || 1;
    const rank = stringRank === 'false';
    const page = parseInt(stringPage);
    return {
      ...querySearch,
      page: page,
      type: type,
      reverse: !rank,
      searchKey: searchKey,
    };
  };

  const getList = () => {
    const data = getFilterMapFromUrl();
    dispatch(createAction('campaignList/getCampaignList')({ ...data }));
  };

  useEffect(() => {
    if (activeTab === URLFragments.list) {
      getList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, location]);

  useEffect(() => {
    dispatch({ type: 'campaignList/campaignResume' });

    dispatch(createAction('brand/getCurrentPageBrands')({ isAll: true }));
    dispatch(createAction('customerGroup/getGroupList')({}));
    dispatch(
      createAction('segments/getSegmentsList')({
        type: 'all',
        isSelectorLoad: true,
      }),
    );
    // dispatch(createAction('levels/getLevelList')({}));

    return () => {
      dispatch({ type: 'campaignList/clearData' });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getButton = (name, className) => {
    return (
      <AuthButton
        title={name}
        className={className}
        action={() => {
          dispatch({ type: 'createCampaign/clearData' });
          history.push({
            pathname: '/campaigns/create',
          });
        }}
        requires={PermissionCodes.addCampaign}
      />
    );
  };

  const buttons = [getButton('Create Campaign', 'btn-further')];

  if (resume.showResumeButton) {
    buttons.unshift(getButton('Resume', 'campaign-resume-button btn-further'));
  }

  const groupActions = [
    {
      name: 'Delete',
      action: (campaign) => setShowPrompt(true),
      requires: PermissionCodes.deleteCampaign,
    },
  ];

  const couponOrderChange = (item, order) => {
    if (item.couponDisplayPriority === order) {
      return;
    }
    dispatch({
      type: 'createCampaign/updateCouponCampaignDisplayPriority',
      payload: {
        id: item.pk,
        couponDisplayPriority: order,
        afterAction: (success) => history.push(location),
      },
    });
  };

  const tabs = [
    {
      name: URLFragments.overview,
      content: <CampaignOverviewTab />,
    },
    isSuperuser ||
    userPermissions?.filter(
      (val) => val.split('_')[1] === PermissionCodes.campaign,
    ).length > 0
      ? {
          name: URLFragments.list,
          content: (
            <BaseTabListContainer
              hideTab={false}
              maxTabWidth={400}
              tabs={TabsConfig}
              groupActions={groupActions}
              pageInfo={pageInfo}
              totalCount={totalCount}
              filter={{
                hasFilter: true,
                componment: Filter,
              }}
              listContent={
                <BaseListContainer
                  fields={listFields}
                  dataList={dataList}
                  totalPage={totalPage}
                  sortable
                  sortDisabled={type !== CampaignType.couponsCampaign}
                  model={'campaignList'}
                  actions={['Edit', 'Duplicate', 'Delete']}
                  permissionGroup={PermissionCodes.campaign}
                  onSortEnd={(oldIndex, newIndex) => {
                    const newData = dataList[newIndex];
                    const movedItem = dataList[oldIndex];
                    couponOrderChange(movedItem, newData.couponDisplayPriority);
                  }}
                  orderDisableEdit={(item) =>
                    item.type !== CampaignType.couponsCampaign
                  }
                  onOrderChangedByEdit={(item, newOrder) => {
                    couponOrderChange(item, newOrder);
                  }}
                  deleteInfo={{
                    data: [],
                    title: 'Campaign',
                    relatedName: '',
                    onComfirm: {},
                    relatedSections: DELETE_RELATED_SECTIONS.CAMPAIGN,
                  }}
                  specificActions={[
                    {
                      fields: [
                        'earningRule',
                        'habitTypeAllCampaignsToEarnRewards',
                      ],
                      conditions: [true],
                      actions: ['Edit'],
                    },
                  ]}
                  specificOtherActions={[
                    {
                      fields: [
                        'earningRule',
                        'habitTypeAllCampaignsToEarnRewards',
                      ],
                      conditions: [true],
                      actions: {},
                    },
                  ]}
                  hasOtherActions={true}
                  customActions={{
                    publish: (item) => {
                      const type = item.isPublished
                        ? 'unPublishCampaign'
                        : 'doPublish';
                      dispatch(
                        createAction(`createCampaign/${type}`)({
                          campaignPk: item.pk,
                          afterAction: () => getList(),
                        }),
                      );
                    },
                    linkStore: (item) => {
                      setSelectedCampaignID(item.pk);
                      setShowEditStorePrompt(true);
                    },
                    moveToFirst: (item) => {
                      couponOrderChange(item, 1);
                    },
                    moveToLast: (item) => {
                      couponOrderChange(item, totalCount);
                    },
                  }}
                />
              }
            />
          ),
        }
      : {},
  ];

  const hideResumePop = (hide) => {
    dispatch({
      type: 'campaignList/resumeAction',
      payload: { hideResumePop: hide },
    });
  };

  return (
    <>
      <CustomListComponent
        caution={{
          detail: 'Campaign list , should edit with the following',
          title: 'Campaigns',
        }}
        onTabChange={(key) => {
          let search = '';
          if (key !== URLFragments.overview) {
            search = queryString.stringify({
              [URLFilterKeys.type]: CampaignType.allTypes,
              [URLFilterKeys.page]: 1,
              [URLFilterKeys.searchKey]: '',
              [URLFilterKeys.rank]: true,
            });
          }
          history.push({ pathname: '/campaigns', hash: key, search: search });
        }}
        defaultActiveKey={activeTab}
        breadcrumb={<CustomBreadcrumb breadcrumbs={campaignListBreads()} />}
        buttons={buttons}
        renderCompare={() => {
          return activeTab === URLFragments.overview ? (
            <DateRangeCompare inCustomListComponent />
          ) : null;
        }}
        renderTabsRight={() => {
          return activeTab === URLFragments.overview ? (
            <DateRangeSelection
              onTab
              applyDateRangeEvent={(startDate, endDate) => {
                dispatch(
                  createAction('overview/getCampaignsData')({
                    startDate: startDate.format('YYYY-MM-DD'),
                    endDate: endDate.format('YYYY-MM-DD'),
                  }),
                );
              }}
              clearDataEvent={() => {
                dispatch(createAction('overview/clearState')());
              }}
            />
          ) : null;
        }}
        tabs={tabs}
      />
      <DeleteCampaignPrompt
        show={showPrompt}
        onClose={() => setShowPrompt(false)}
        onConfirm={() => {
          setShowPrompt(false);
          dispatch(
            createAction('createCampaign/deleteCampaigns')({
              afterAction: () => {
                history.push(location);
              },
            }),
          );
        }}
      />
      <BasePrompt
        show={resume.showResumePop}
        closeAction={() => hideResumePop(false)}
        rightButton={{
          text: 'Resume',
          action: () => {
            hideResumePop(false);
            history.push('/campaigns/create');
          },
        }}
        leftButton={{
          text: `Dismiss and don't remind me again`,
          action: () => hideResumePop(true),
        }}
        title={'You have unfinished campaign '}
        description={`You have unfinished campaign before. You can resume it by click “Resume” button at the top of the page`}
      />
      <CampaignEditStorePrompt
        campaignID={selectedCampaignID}
        show={showEditStorePrompt}
        closeAction={() => {
          setShowEditStorePrompt(false);
          setSelectedCampaignID(null);
        }}
      />
    </>
  );
}

export default CampaignList;
